import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, getISOWeek, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { QueryMode, StorePerformanceItem, ResultsData, QuestionnairePerformance, PerformanceTab, ProcessStatusQueryParams, PieChartStoreData, PieChartPaginationInfo } from '../types'
import { Constants } from '../../../../../../../utils/constants'
import { isValidDate } from '../utils/periodValidation'
import { buildQuestionnaireIds } from '../utils/buildQueryParams'
import { SESSION_STORAGE_KEY, SESSION_STORAGE_RESULTS_KEY, SESSION_STORAGE_VERSIONS_KEY, SCROLL_DELAY_MS } from '../constants'
import { RedirectedIds } from '../../../../../typing'

interface UseNavigationProps {
  canConsult: boolean
  queryMode: QueryMode
  selectedCompany: StorePerformanceItem | null
  selectedUnits: StorePerformanceItem[]
  selectedQuestionnaires: StorePerformanceItem[]
  searchUnit: string
  searchQuestionnaire: string
  periodFrom: Date | null
  periodTo: Date | null
  periodWeek: string[]
  periodYear: number | null
  filterDivision: StorePerformanceItem[]
  filterZone: StorePerformanceItem[]
  filterUser: StorePerformanceItem[]
  filterRole: StorePerformanceItem[]
  appliedPeriodFrom: Date | null
  appliedPeriodTo: Date | null
  appliedPeriodWeek: string[]
  appliedPeriodYear: number
  hasQueried: boolean
  resultsData: ResultsData
  performanceData: QuestionnairePerformance[]
  consultedItems: StorePerformanceItem[]
  selectedResultIds: string[]
  activeTab: PerformanceTab
  chartType: 'bar' | 'line' | 'pie'
  appliedProcessStatusParams: ProcessStatusQueryParams | null
  appliedCriticalRange: [number, number]
  lastConsultTime: Date | null
  pieChartData: PieChartStoreData[]
  pieChartPagination: PieChartPaginationInfo
  resultsPage: number
  selectedVersionsByQuestionnaire: Record<string, string[]>
  showVersionsPanel: boolean
  activeVersionQuestionnaireId: string | null
  setFilterDivision: (items: StorePerformanceItem[]) => void
  setFilterZone: (items: StorePerformanceItem[]) => void
  setFilterUser: (items: StorePerformanceItem[]) => void
  setFilterRole: (items: StorePerformanceItem[]) => void
}

interface UseNavigationReturn {
  restoredFromNavigation: boolean
  handleNavigateToProgramation: () => void
}

const useNavigation = ({
  canConsult,
  queryMode,
  selectedCompany,
  selectedUnits,
  selectedQuestionnaires,
  searchUnit,
  searchQuestionnaire,
  periodFrom,
  periodTo,
  periodWeek,
  periodYear,
  filterDivision,
  filterZone,
  filterUser,
  filterRole,
  appliedPeriodFrom,
  appliedPeriodTo,
  appliedPeriodWeek,
  appliedPeriodYear,
  hasQueried,
  resultsData,
  performanceData,
  consultedItems,
  selectedResultIds,
  activeTab,
  chartType,
  appliedProcessStatusParams,
  appliedCriticalRange,
  lastConsultTime,
  pieChartData,
  pieChartPagination,
  resultsPage,
  selectedVersionsByQuestionnaire,
  showVersionsPanel,
  activeVersionQuestionnaireId,
  setFilterDivision,
  setFilterZone,
  setFilterUser,
  setFilterRole,
}: UseNavigationProps): UseNavigationReturn => {
  const navigate = useNavigate()
  const [restoredFromNavigation, setRestoredFromNavigation] = useState(false)

  useEffect(() => {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (raw) {
      try {
        const saved = JSON.parse(raw)
        if (saved.filterDivision) setFilterDivision(saved.filterDivision)
        if (saved.filterZone) setFilterZone(saved.filterZone)
        if (saved.filterUser) setFilterUser(saved.filterUser)
        if (saved.filterRole) setFilterRole(saved.filterRole)
        sessionStorage.removeItem(SESSION_STORAGE_KEY)
        setRestoredFromNavigation(true)
      } catch {
        sessionStorage.removeItem(SESSION_STORAGE_KEY)
      }
    }
  }, [])

  useEffect(() => {
    if (restoredFromNavigation) {
      const timer = setTimeout(() => {
        const el = document.getElementById(RedirectedIds.STORE_PERFORMANCE)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, SCROLL_DELAY_MS)

      return () => clearTimeout(timer)
    }
  }, [restoredFromNavigation])

  const handleNavigateToProgramation = useCallback(() => {
    if (!canConsult) return

    const queryParams = new URLSearchParams()

    const hasDateFrom = isValidDate(appliedPeriodFrom)
    const hasDateTo = isValidDate(appliedPeriodTo)
    if (hasDateFrom || hasDateTo) {
      if (hasDateFrom) {
        queryParams.set('from', format(appliedPeriodFrom, Constants.dateFormat, { locale: es }))
      }
      if (hasDateTo) {
        queryParams.set('to', format(appliedPeriodTo, Constants.dateFormat, { locale: es }))
      }
      queryParams.set('intervale', '1d')
    } else if (appliedPeriodWeek.length > 0 && appliedPeriodYear) {
      const minWeek = Math.min(...appliedPeriodWeek.map(Number))
      const maxWeek = Math.max(...appliedPeriodWeek.map(Number))
      let fromDate = parseISO(`${appliedPeriodYear}-W${String(minWeek).padStart(2, '0')}-1`)
      let toDate = parseISO(`${appliedPeriodYear}-W${String(maxWeek).padStart(2, '0')}-7`)
      const yearStart = new Date(appliedPeriodYear, 0, 1)
      const yearEnd = new Date(appliedPeriodYear, 11, 31)
      if (fromDate < yearStart) fromDate = yearStart
      if (toDate > yearEnd) toDate = yearEnd
      queryParams.set('from', format(fromDate, Constants.dateFormat, { locale: es }))
      queryParams.set('to', format(toDate, Constants.dateFormat, { locale: es }))
      queryParams.set('intervale', '1w')
    } else {
      const now = new Date()
      const cw = getISOWeek(now)
      const cy = now.getFullYear()
      let fromDate = parseISO(`${cy}-W${String(cw).padStart(2, '0')}-1`)
      let toDate = parseISO(`${cy}-W${String(cw).padStart(2, '0')}-7`)
      const yearStart = new Date(cy, 0, 1)
      const yearEnd = new Date(cy, 11, 31)
      if (fromDate < yearStart) fromDate = yearStart
      if (toDate > yearEnd) toDate = yearEnd
      queryParams.set('from', format(fromDate, Constants.dateFormat, { locale: es }))
      queryParams.set('to', format(toDate, Constants.dateFormat, { locale: es }))
      queryParams.set('intervale', '1w')
    }

    if (selectedUnits.length > 0) {
      queryParams.set('units', selectedUnits.map((u) => u.id).join(','))
    }
    const questionnaireIds = buildQuestionnaireIds(selectedQuestionnaires, selectedVersionsByQuestionnaire)
    if (questionnaireIds.length > 0) {
      queryParams.set('questionnaires', questionnaireIds.join(','))
    }
    if (filterDivision.length > 0) {
      queryParams.set('divisions', filterDivision.map((d) => d.value).join(','))
    }
    if (filterZone.length > 0) {
      queryParams.set('zones', filterZone.map((z) => z.value).join(','))
    }
    if (filterUser.length > 0) {
      queryParams.set('users', filterUser.map((u) => u.value).join(','))
    }
    if (filterRole.length > 0) {
      queryParams.set('roleIds', filterRole.map((r) => r.value).join(','))
    }
    
    queryParams.set('questionnaireStatus', '8')

    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
      queryMode,
      selectedCompany,
      selectedUnits,
      selectedQuestionnaires,
      searchUnit,
      searchQuestionnaire,
      periodFrom: periodFrom ? periodFrom.toISOString() : null,
      periodTo: periodTo ? periodTo.toISOString() : null,
      periodWeek,
      periodYear,
      filterDivision,
      filterZone,
      filterUser,
      filterRole,
    }))

    sessionStorage.setItem(SESSION_STORAGE_RESULTS_KEY, JSON.stringify({
      hasQueried,
      resultsData,
      performanceData,
      consultedItems,
      selectedResultIds,
      activeTab,
      chartType,
      appliedProcessStatusParams,
      appliedCriticalRange,
      lastConsultTime: lastConsultTime ? lastConsultTime.toISOString() : null,
      pieChartData,
      pieChartPagination,
      resultsPage,
    }))

    sessionStorage.setItem(SESSION_STORAGE_VERSIONS_KEY, JSON.stringify({
      selectedVersionsByQuestionnaire,
      showVersionsPanel,
      activeVersionQuestionnaireId,
    }))

    navigate(`/dashboard/reports/programmed?${queryParams.toString()}`)
  }, [
    canConsult,
    appliedPeriodFrom,
    appliedPeriodTo,
    appliedPeriodWeek,
    appliedPeriodYear,
    selectedUnits,
    selectedQuestionnaires,
    searchUnit,
    searchQuestionnaire,
    filterDivision,
    filterZone,
    filterUser,
    filterRole,
    navigate,
    queryMode,
    selectedCompany,
    periodFrom,
    periodTo,
    periodWeek,
    periodYear,
    hasQueried,
    resultsData,
    performanceData,
    consultedItems,
    selectedResultIds,
    activeTab,
    chartType,
    appliedProcessStatusParams,
    appliedCriticalRange,
    lastConsultTime,
    pieChartData,
    pieChartPagination,
    resultsPage,
    selectedVersionsByQuestionnaire,
    showVersionsPanel,
    activeVersionQuestionnaireId,
  ])

  return {
    restoredFromNavigation,
    handleNavigateToProgramation,
  }
}

export { useNavigation }
